import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { subMonths } from 'date-fns';
import uniq from 'lodash/uniq';

import { RootState } from '$store/rootReducer';
import {
  SetUnAnsweredCountAction,
  BuyMessageAction,
  CallAction,
  CallCancelAction,
  CallSendAcceptAction,
  CallStatus,
  CallType,
  IceServer,
  ChatSetFormTextAction,
  ChatSetReplyToAction,
  ClearFormAction,
  CreateInvoiceAction,
  DeleteDialogAction,
  DeleteMessageAction,
  ImState,
  LoadAvgInvoicePriceAction,
  IncomingCallAction,
  LoadBroadcastStats,
  LoadChatAction,
  LoadMoreDialogsAction,
  LoadMoreHistoryAction,
  MarkMessageAsBoughtAction,
  ReadHistoryAction,
  RemoveDialogAction,
  ReportMessageAction,
  ResetTypingAction,
  RetrySendAction,
  SendInstantMessageAction,
  SendMessageAction,
  SendTipFromBalanceAction,
  SendTipsAction,
  SetAvgInvoicePriceAction,
  SendTypingAction,
  SetBroadcastExcludeAction,
  SetBroadcastMessageTextAction,
  SetBroadcastPaidAction,
  SetBroadcastPriceAction,
  SetBroadcastRollbackPreviousAction,
  SetBroadcastSearchTextAction,
  SetBroadcastSendToAction,
  SetBroadcastSkipUpToTopDialogsAction,
  SetBroadcastStatsAction,
  SetBroadcastStatsLoadingAction,
  SetCallPeerIdAction,
  SetCallRemoteDescription,
  SetCallStatusAction,
  SetCallTypeAction,
  SetChatFailedAction,
  SetDialogsAction,
  SetHistoryAction,
  SetIsVideoUploadingAction,
  SetMessageAction,
  SetMoreBroadcastStats,
  SetTonRateAction,
  SetTypingAction,
  SetUnsentBroadcastAction,
  SetWelcomeMessageAction,
  SetWriteIdsAction,
  TelegramRetryAction,
  UnsendBroadcastAction,
  UpdateFormMessageAction,
  UpdateFormPriceAction,
  SetWelcomeMessageLoadingAction,
  SetHasWelcomeMessageAction,
  SetTelegramSheetVisibleAction,
  SetChooseChatTelegramSheetVisibleAction,
  SetTelegramChatsAction,
  SetTelegramChatIdsAction,
  UpdateFormLangAction,
  SetBroadcastListAction,
  SetBroadcastRecipientAction,
  SetBroadcastSkipIdsAction,
  SetBroadcastExcludeUserIdsAction,
  SetBroadcastExcludeUserIdAction,
  SetBroadcastFilterActiveAction,
  BroadcastFilterName,
  SetBroadcastFilterLanguageAction,
  SetBroadcastFilterDateAction,
  BroadcastFilters,
  LoadWelcomeMessageAction,
  EditWelcomeMessageAction,
  WelcomeMessageType,
  SetShowSubscriptionRequiredMessageAction,
  SendValentineDayStickerAction,
  SendBroadcastTotalRecipientsCountAction,
  SetIsListsLoadingAction,
  BroadcastStatsStatusType,
  ShowChooseChatTelegramSheetWithTypeAction,
  UpdateFormCurrencyAction,
  UpdateFormPriceWithoutDiscountAction,
  SetServiceAccountsAction,
  SetAsPeerIdAction,
} from '$store/im/interface';
import { AttachItem, DialogModel, ListInfoModel } from '$store/models';
import { transliterate } from '$utils';
import router from '$navigation/router';
import { getLanguage } from '$translation';

const monthAgo = subMonths(new Date(), 1);

export const initialStateFilters: BroadcastFilters = {
  subscribeDate: [monthAgo, new Date()],
  sponsorSubscribeDate: [monthAgo, new Date()],
  registrationDate: [monthAgo, new Date()],
  languages: [getLanguage()],
};

const initialState: ImState = {
  isDialogsLoading: true,
  isDialogsLoadingMore: false,
  lists: [],
  nextFrom: '',
  dialogsList: [],
  dialogs: {},
  history: {},
  typing: {},
  info: {},
  isWriteLoading: true,
  writeIds: [],
  forms: {},
  tonRate: null,
  spendLevel: 0,
  serviceAccounts: [],
  isServiceAccountsLoading: false,
  asPeerId: +(sessionStorage.getItem('im_asPeerId') || 0),
  broadcastStats: {
    isLoading: true,
    isLoadingMore: false,
    nextFrom: '',
    broadcasts: [],
  },
  broadcast: {
    totalRecipientsCount: 0,
    lists: [],
    activeFilters: {},
    appliedActiveFilters: {},
    filters: initialStateFilters,
    appliedFilters: initialStateFilters,
    isListsLoading: false,
    isRecipientsLoading: false,
    followerIds: [],
    telegramIds: [],
    eroticSubscribers: [],
    recipientIds: [],
    sponsorIds: [],
    searchText: '',
    sendTo: [],
    exclude: [],
    users: [],
    userNames: {},
    userIds: [],
    excludeUserIds: [],
    skipIds: [],
    price: '',
    priceWithoutDiscount: '',
    text: '',
    rollbackPrevious: true,
    skipUpToTopDialogs: true,
    isPaid: false,
  },
  telegram: {
    isTelegramSheetVisible: false,
    isChooseChatTelegramSheetVisible: false,
    isChatsLoading: true,
    chooseChatTelegramSheetType: 'post',
    chats: [],
    chatIds: [],
  },
  welcomeMessage: {
    [WelcomeMessageType.FOLLOWER]: {
      text: '',
      price: '',
      priceWithoutDiscount: '',
      loadingStatus: 'loading',
      isPaid: false,
      isEnabled: false,
    },
    [WelcomeMessageType.SPONSOR]: {
      text: '',
      price: '',
      priceWithoutDiscount: '',
      loadingStatus: 'loading',
      isPaid: false,
      isEnabled: false,
    },
    [WelcomeMessageType.TG_SUBSCRIBER]: {
      text: '',
      price: '',
      priceWithoutDiscount: '',
      loadingStatus: 'loading',
      isPaid: false,
      isEnabled: false,
    },
  },
  isVideoUploading: false,
  section: 'all',
  suggestions: [],
  avgInvoicePrice: null,
  call: {
    type: null,
    status: null,
    peerId: 0,
    remoteDescription: null,
    localStream: null,
    remoteStream: null,
    callId: null,
    iceServers: null,
  },
  hasWelcomeMessage: false,
  showSubscriptionRequiredMessage: {},
  unAnsweredCount: 0,
  aiOptions: [],
};

export const { actions, reducer } = createSlice({
  name: 'im',
  initialState,
  reducers: {
    setAIOptions(state, action: PayloadAction<string[]>) {
      state.aiOptions = action.payload;
    },
    loadBroadcastLists(state) {
      state.broadcast.isListsLoading = true;
    },
    setIsListsLoading(state, action: SetIsListsLoadingAction) {
      state.broadcast.isListsLoading = action.payload;
    },
    setIsRecipientsLoading(state, action: { payload: boolean }) {
      state.broadcast.isRecipientsLoading = action.payload;
    },
    setLists(state, action: PayloadAction<ListInfoModel[]>) {
      state.lists = action.payload;
    },
    setBroadcastList(state, action: SetBroadcastListAction) {
      state.broadcast.lists = action.payload;
      state.broadcast.isListsLoading = false;
    },
    loadBroadcastStats(state) {
      state.broadcastStats.isLoading = true;
    },
    loadMoreBroadcastStats(state, action: LoadBroadcastStats) {
      state.broadcastStats.isLoadingMore = true;
    },
    setMoreBroadcastStats(state, action: SetMoreBroadcastStats) {
      const { broadcasts, nextFrom } = action.payload;
      state.broadcastStats.isLoading = false;
      state.broadcastStats.isLoadingMore = false;
      state.broadcastStats.nextFrom = nextFrom;
      state.broadcastStats.broadcasts = [
        ...state.broadcastStats.broadcasts,
        ...broadcasts,
      ];
    },
    setBroadcastStatsLoading(state, action: SetBroadcastStatsLoadingAction) {
      state.broadcastStats.isLoading = action.payload;
    },
    setBroadcastStats(state, action: SetBroadcastStatsAction) {
      state.broadcastStats = {
        ...state.broadcastStats,
        ...action.payload,
      };
    },
    unsendBroadcast(_, action: UnsendBroadcastAction) {},
    setUnsentBroadcast(state, action: SetUnsentBroadcastAction) {
      state.broadcastStats.broadcasts = state.broadcastStats.broadcasts.map((b) => {
        if (b.id === action.payload.id) {
          return {
            ...b,
            status: BroadcastStatsStatusType.ROLLBACK,
            unsent: action.payload.unsent,
          };
        }
        return b;
      });
    },
    setBroadcastSendTo(state, action: SetBroadcastSendToAction) {
      state.broadcast.sendTo = action.payload;
    },
    setBroadcastExclude(state, action: SetBroadcastExcludeAction) {
      state.broadcast.exclude = action.payload;
    },
    sendBroadcastMessage() {},
    sendBroadcastTotalRecipientsCount(
      state,
      action: SendBroadcastTotalRecipientsCountAction,
    ) {
      state.broadcast.totalRecipientsCount = action.payload;
    },
    resetBroadcast(state) {
      state.broadcast.recipientIds = [];
      state.broadcast.text = '';
      state.broadcast.searchText = '';
      state.broadcast.price = '';
      state.broadcast.sendTo = [];
      state.broadcast.exclude = [];
      state.broadcast.filters = initialStateFilters;
      state.broadcast.appliedFilters = initialStateFilters;
      state.broadcast.appliedActiveFilters = {};
      state.broadcast.activeFilters = {};
    },
    setBroadcastRecipient(state, action: SetBroadcastRecipientAction) {
      const users = action.payload;
      state.broadcast.recipientIds = Object.values(users).map((u) => u.id);
      state.broadcast.recipientIds.forEach((id) => {
        const name = (users[id].firstName + ' ' + users[id].lastName).toLowerCase();
        state.broadcast.userNames[id] = [name, transliterate(name)];
      });
    },
    filterBroadcastUsers(state) {
      const { searchText, userNames, recipientIds } = state.broadcast;
      if (searchText.length) {
        state.broadcast.userIds = recipientIds.filter((id) => {
          return userNames[id].some((name) => name.includes(searchText.toLowerCase()));
        });
      } else {
        state.broadcast.userIds = recipientIds;
      }
    },
    setBroadcastSearchText(state, action: SetBroadcastSearchTextAction) {
      state.broadcast.searchText = action.payload;
    },
    resetBroadcastSearchText(state) {
      state.broadcast.searchText = '';
    },
    setBroadcastMessageText(state, action: SetBroadcastMessageTextAction) {
      state.broadcast.text = action.payload;
    },
    setBroadcastPrice(state, action: SetBroadcastPriceAction) {
      state.broadcast.price = action.payload;
    },
    setBroadcastPriceWithoutDiscount(state, action: SetBroadcastPriceAction) {
      state.broadcast.priceWithoutDiscount = action.payload;
    },
    setBroadcastPaid(state, action: SetBroadcastPaidAction) {
      state.broadcast.isPaid = action.payload;
    },
    setBroadcastRollbackPrevious(state, action: SetBroadcastRollbackPreviousAction) {
      state.broadcast.rollbackPrevious = action.payload;
    },
    setBroadcastSkipUpToTopDialogs(state, action: SetBroadcastSkipUpToTopDialogsAction) {
      state.broadcast.skipUpToTopDialogs = action.payload;
    },
    setBroadcastSkipIds(state, action: SetBroadcastSkipIdsAction) {
      state.broadcast.skipIds = action.payload;
    },
    setBroadcastExcludeUserIds(state, action: SetBroadcastExcludeUserIdsAction) {
      state.broadcast.excludeUserIds = action.payload;
    },
    setBroadcastExcludeUserId(state, action: SetBroadcastExcludeUserIdAction) {
      const { id, value } = action.payload;
      if (value) {
        state.broadcast.excludeUserIds.push(id);
      } else {
        state.broadcast.excludeUserIds = state.broadcast.excludeUserIds.filter(
          (item) => item !== id,
        );
      }
    },
    setBroadcastFilterActive(state, action: SetBroadcastFilterActiveAction) {
      const { filterName, isActive } = action.payload;
      state.broadcast.activeFilters[filterName] = isActive;
    },
    setBroadcastFilterLanguage(state, action: SetBroadcastFilterLanguageAction) {
      state.broadcast.filters.languages = action.payload;
      state.broadcast.activeFilters[BroadcastFilterName.LANGUAGES] =
        action.payload.length > 0;
    },
    setBroadcastFilterRegistrationDate(state, action: SetBroadcastFilterDateAction) {
      state.broadcast.activeFilters[BroadcastFilterName.REGISTRATION_DATE] = true;
      state.broadcast.filters.registrationDate = action.payload;
    },
    setBroadcastFilterSponsorSubscribeDate(state, action: SetBroadcastFilterDateAction) {
      state.broadcast.activeFilters[BroadcastFilterName.SPONSOR_SUBSCRIBE_DATE] = true;
      state.broadcast.filters.sponsorSubscribeDate = action.payload;
    },
    setBroadcastFilterSubscribeDate(state, action: SetBroadcastFilterDateAction) {
      state.broadcast.activeFilters[BroadcastFilterName.SUBSCRIBE_DATE] = true;
      state.broadcast.filters.subscribeDate = action.payload;
    },
    applyBroadcastFilters(state) {
      state.broadcast.appliedFilters = state.broadcast.filters;
      state.broadcast.appliedActiveFilters = state.broadcast.activeFilters;
    },
    cancelBroadcastFilters(state) {
      state.broadcast.filters = state.broadcast.appliedFilters;
      state.broadcast.activeFilters = state.broadcast.appliedActiveFilters;
    },
    resetBroadcastFilters(state) {
      state.broadcast.activeFilters = {};
      state.broadcast.filters = initialStateFilters;
    },
    loadDialogs(state, _action: PayloadAction<{ asPeerId?: number } | undefined>) {
      const section = router.getState().params.section || 'all';
      if (state.section !== section) {
        state.dialogs = {};
        state.dialogsList = [];
      }
      state.isDialogsLoading = true;
    },
    setDialogsList(state, action: PayloadAction<number[]>) {
      state.dialogsList = uniq(action.payload);
    },
    addDialogsList(state, action: PayloadAction<number[]>) {
      const exists: Record<number, boolean> = {};
      for (const id of state.dialogsList) {
        exists[id] = true;
      }
      const next = [...state.dialogsList];
      for (const id of action.payload) {
        if (exists[id]) continue;
        exists[id] = true;
        next.push(id);
      }
      state.dialogsList = next;
    },
    setDialogs(state, action: SetDialogsAction) {
      const { dialogs, messages, nextFrom, section, suggestions } = action.payload;

      const history = { ...state.history };
      const dialogsMap: { [index: number]: DialogModel } = {};
      for (let dialog of dialogs) {
        const message = messages[dialog.lastMsgId];
        dialogsMap[dialog.peerId] = dialog;

        state.info[dialog.peerId] = {
          ...state.info[dialog.peerId],
          isSponsor: dialog.sponsor,
        };

        if (dialog.lastMsgId > 0 && message) {
          history[dialog.peerId] = {
            ...(history[dialog.peerId] || {}),
            [message.id]: message,
          };
        }
      }

      if (section) {
        state.section = section;
      }
      state.dialogs = {
        ...state.dialogs,
        ...dialogsMap,
      };
      state.history = history;
      state.isDialogsLoading = false;
      state.isDialogsLoadingMore = false;
      if (suggestions) {
        state.suggestions = suggestions;
      }

      if (nextFrom !== undefined) {
        state.nextFrom = nextFrom;
      }
    },
    loadMoreDialogs(state, action: LoadMoreDialogsAction) {
      state.isDialogsLoadingMore = true;
    },
    loadChat(state, action: LoadChatAction) {
      const { peerId } = action.payload;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        isLoading: true,
        isFailed: false,
      };
    },
    loadMoreHistory(state, action: LoadMoreHistoryAction) {
      const { peerId } = action.payload;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        isLoadingMore: true,
      };
    },
    setTyping(state, action: SetTypingAction) {
      const { peerId, typing } = action.payload;
      state.typing[peerId] = typing;
    },
    resetTyping(state, action: ResetTypingAction) {
      state.typing[action.payload] = false;
    },
    sendTyping(_state, _action: SendTypingAction) {},
    chatSetFormText(state, action: ChatSetFormTextAction) {
      const { peerId, text } = action.payload;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        text,
      };
    },
    chatSetReplyTo(state, action: ChatSetReplyToAction) {
      const { peerId, replyTo } = action.payload;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        replyTo,
      };
    },
    sendMessage(_state, _action: SendMessageAction) {},
    sendInstantMessage(_state, _action: SendInstantMessageAction) {},
    setHistory(state, action: SetHistoryAction) {
      const { peerId, history, nextFrom, replace = false } = action.payload;
      state.history[peerId] = {
        ...(replace ? {} : state.history[peerId] || {}),
        ...history,
      };

      state.spendLevel = action.payload.spendLevel || 0;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        isLoading: false,
        isLoadingMore: false,
        nextFrom: nextFrom !== undefined ? nextFrom : state.info[peerId]?.nextFrom,
      };
    },
    setChatFailed(state, action: SetChatFailedAction) {
      const { peerId, reason } = action.payload;

      state.info[peerId] = {
        ...(state.info[peerId] || {}),
        isLoading: false,
        isFailed: true,
        failReason: reason,
      };
    },
    setMessage(state, action: SetMessageAction) {
      const { peerId, message, messageId } = action.payload;

      const messages = {
        ...(state.history[peerId] || {}),
        [message.id]: message,
      };

      if (messageId !== message.id) {
        delete messages[messageId];
      }

      state.history[peerId] = messages;
    },
    retrySend(_state, _action: RetrySendAction) {},
    readHistory(_state, _action: ReadHistoryAction) {},
    loadWrite(state, _action: PayloadAction<{ asPeerId?: number } | undefined>) {
      state.isWriteLoading = true;
    },
    setWriteIds(state, action: SetWriteIdsAction) {
      state.isWriteLoading = false;
      state.writeIds = action.payload;
    },
    deleteDialog(_state, _action: DeleteDialogAction) {},
    removeDialog(state, action: RemoveDialogAction) {
      delete state.dialogs[action.payload];
      delete state.history[action.payload];
      state.dialogsList = state.dialogsList.filter((id) => id !== action.payload);
    },
    deleteMessage(_state, _action: DeleteMessageAction) {},
    reportMessage(_, __: ReportMessageAction) {},
    telegramRetry(_, __: TelegramRetryAction) {},
    removeMessage(state, action: DeleteMessageAction) {
      const { id, peerId } = action.payload;
      if (state.history[peerId]) {
        delete state.history[peerId][id];
      }
    },
    deleteUnsentMessage(_state, _action: DeleteMessageAction) {},
    createInvoice(_, __: CreateInvoiceAction) {},
    createTelegramInvoice() {},
    chooseTelegramChat() {},
    editWelcomeMessage(_, __: EditWelcomeMessageAction) {},
    buyMessage(_, __: BuyMessageAction) {},
    sendTips(_, __: SendTipsAction) {},
    markMessageAsBought(state, action: MarkMessageAsBoughtAction) {
      const { peerId, messageId } = action.payload;
      const message = state.history[peerId][messageId];

      if (message) message.isHidden = false;
    },
    updateFormMessage(state, action: UpdateFormMessageAction) {
      const { formId, message } = action.payload;

      state.forms[formId] = {
        ...(state.forms[formId] || {
          price: '',
        }),
        message,
      };
    },
    updateFormLang(state, action: UpdateFormLangAction) {
      const { formId, lang } = action.payload;

      state.forms[formId] = {
        ...(state.forms[formId] || {
          lang: '',
        }),
        lang,
      };
    },
    updateFormCurrency(state, action: UpdateFormCurrencyAction) {
      const { formId, currency } = action.payload;

      state.forms[formId] = {
        ...(state.forms[formId] || {
          currency: '',
        }),
        currency,
      };
    },
    updateFormPrice(state, { payload: { formId, price } }: UpdateFormPriceAction) {
      state.forms[formId] = {
        ...(state.forms[formId] || {
          message: '',
        }),
        price,
      };
    },
    updateFormPriceWithoutDiscount(
      state,
      { payload: { formId, priceWithoutDiscount } }: UpdateFormPriceWithoutDiscountAction,
    ) {
      state.forms[formId] = {
        ...(state.forms[formId] || {
          message: '',
        }),
        priceWithoutDiscount,
      };
    },
    sendTipFromBalance(_, __: SendTipFromBalanceAction) {},
    sendTipFromCard(_, __: SendTipFromBalanceAction) {},
    loadTonRate(_) {},
    setTonRate(state, action: SetTonRateAction) {
      state.tonRate = action.payload;
    },
    loadWelcomeMessage(_, __: LoadWelcomeMessageAction) {},
    setWelcomeMessageLoading(state, action: SetWelcomeMessageLoadingAction) {
      const { loading, type } = action.payload;
      state.welcomeMessage[type].loadingStatus = loading;
    },
    setWelcomeMessage(state, action: SetWelcomeMessageAction) {
      const { type, data } = action.payload;
      state.welcomeMessage[type] = data;
    },
    clearForm(state, action: ClearFormAction) {
      if (!state.isVideoUploading) {
        state.forms[action.payload] = {
          message: '',
          price: '',
          priceWithoutDiscount: '',
          lang: '',
          currency: '',
        };
      }
    },
    setIsVideoUploading(state, action: SetIsVideoUploadingAction) {
      state.isVideoUploading = action.payload;
    },
    loadAvgInvoicePrice(_, __: LoadAvgInvoicePriceAction) {},
    setAvgInvoicePrice(state, action: SetAvgInvoicePriceAction) {
      state.avgInvoicePrice = action.payload;
    },
    clearAdditionalInfo(state) {
      state.avgInvoicePrice = null;
    },
    setCallStatus(state, action: SetCallStatusAction) {
      state.call.status = action.payload;
    },
    setCallType(state, action: SetCallTypeAction) {
      state.call.type = action.payload;
    },
    setCallPeerId(state, action: SetCallPeerIdAction) {
      state.call.peerId = action.payload;
      state.call.status = CallStatus.RINGING;
    },
    callAccept(state) {
      state.call.status = CallStatus.CONNECTING;
    },
    callReject(state) {
      state.call.status = CallStatus.REJECTED;
    },
    callCancel(state, action: CallCancelAction) {
      state.call.peerId = 0;
      state.call.status = null;
      state.call.callId = null;
      state.call.iceServers = null;
    },
    callSendAccept(state, action: CallSendAcceptAction) {},
    setCallRemoteDescription(state, action: SetCallRemoteDescription) {
      state.call.remoteDescription = action.payload;
    },
    setCallIceConfig(
      state,
      action: PayloadAction<{ callId: string | null; iceServers: IceServer[] }>,
    ) {
      state.call.callId = action.payload.callId;
      state.call.iceServers = action.payload.iceServers;
    },
    call(state, action: CallAction) {
      state.call.type = CallType.OUTGOING;
      state.call.peerId = action.payload.peerId;
      state.call.status = CallStatus.RINGING;
    },
    incomingCall(state, action: IncomingCallAction) {
      state.call.callId = action.payload.callId || null;
      state.call.iceServers = action.payload.iceServers || null;
    },
    hangup(state) {
      state.call.peerId = 0;
      state.call.status = null;
      state.call.callId = null;
      state.call.iceServers = null;
    },
    closeConnectionCall(state) {
      if (state.call.status === CallStatus.COMPLETED) {
        state.call.status = null;
        state.call.peerId = 0;
      }
      state.call.status = CallStatus.CANCELED;
    },
    setHasWelcomeMessage(state, action: SetHasWelcomeMessageAction) {
      state.hasWelcomeMessage = action.payload;
    },
    setTelegramSheetVisible(state, action: SetTelegramSheetVisibleAction) {
      state.telegram.isTelegramSheetVisible = action.payload;
    },
    telegramGetChats(state) {
      state.telegram.isChatsLoading = true;
    },
    setTelegramChats(state, action: SetTelegramChatsAction) {
      state.telegram.isChatsLoading = false;
      state.telegram.chats = action.payload;
    },
    setTelegramChatIds(state, action: SetTelegramChatIdsAction) {
      state.telegram.chatIds = action.payload;
    },
    setChooseChatTelegramSheetVisible(
      state,
      action: SetChooseChatTelegramSheetVisibleAction,
    ) {
      state.telegram.isChooseChatTelegramSheetVisible = action.payload;
      state.telegram.chooseChatTelegramSheetType = 'post';
      state.telegram.chats = [];
      state.telegram.chatIds = [];
      state.telegram.isChatsLoading = true;
    },
    showChooseChatTelegramSheetWithType(
      state,
      action: ShowChooseChatTelegramSheetWithTypeAction,
    ) {
      state.telegram.isChooseChatTelegramSheetVisible = true;
      state.telegram.chooseChatTelegramSheetType = action.payload;
      state.telegram.chats = [];
      state.telegram.chatIds = [];
      state.telegram.isChatsLoading = true;
    },
    setShowSubscriptionRequiredMessage(
      state,
      action: SetShowSubscriptionRequiredMessageAction,
    ) {
      state.showSubscriptionRequiredMessage[action.payload.peerId] = action.payload.show;
    },
    sendValentineDaySticker(_, __: SendValentineDayStickerAction) {},
    setUnAnsweredCount(state, action: SetUnAnsweredCountAction) {
      state.unAnsweredCount = action.payload;
    },
    loadServiceAccounts(state) {
      state.isServiceAccountsLoading = true;
    },
    setServiceAccounts(state, action: SetServiceAccountsAction) {
      state.serviceAccounts = action.payload;
      state.isServiceAccountsLoading = false;
    },
    setAsPeerId(state, action: SetAsPeerIdAction) {
      const newAsPeerId = action.payload;
      if (state.asPeerId !== newAsPeerId) {
        state.asPeerId = newAsPeerId;
        state.dialogs = {};
        state.dialogsList = [];
        state.history = {};
        state.info = {};
        state.nextFrom = '';
        sessionStorage.setItem('im_asPeerId', String(newAsPeerId));
      }
    },
    resetAsPeerId(state) {
      if (state.asPeerId !== 0) {
        state.asPeerId = 0;
        state.dialogs = {};
        state.dialogsList = [];
        state.history = {};
        state.info = {};
        state.nextFrom = '';
        sessionStorage.removeItem('im_asPeerId');
      }
    },
  },
});

export { reducer as imReducer, actions as imActions };

export const imSelector = (state: RootState) => state.im;
export const imSpendLevelSelector = (state: RootState) => state.im.spendLevel;
export const imBroadcastStatsSelector = (state: RootState) => state.im.broadcastStats;
export const imBroadcastReceiversCountSelector = (state: RootState) => {
  if (state.im.broadcast.totalRecipientsCount > 0) {
    return (
      state.im.broadcast.totalRecipientsCount -
      uniq([...state.im.broadcast.skipIds, ...state.im.broadcast.excludeUserIds]).length
    );
  }
  return 0;
};
// state.im.broadcast.recipientIds.filter(
//   (id) =>
//     !state.im.broadcast.skipIds.includes(id) &&
//     !state.im.broadcast.excludeUserIds.includes(id),
// ).length;

export const imCallSelector = (state: RootState) => state.im.call;

export const imTelegramSelector = (state: RootState) => state.im.telegram;
export const imBroadcastSelector = (state: RootState) => state.im.broadcast;
export const imBroadcastFilterSelector = (state: RootState) => state.im.broadcast.filters;
export const imBroadcastAppliedFilterSelector = (state: RootState) =>
  state.im.broadcast.appliedFilters;
export const unAnsweredCountSelector = (state: RootState) => state.im.unAnsweredCount;

export const imListsSelector = (state: RootState) => state.im.lists;
export const imServiceAccountsSelector = (state: RootState) => state.im.serviceAccounts;
export const imIsServiceAccountsLoadingSelector = (state: RootState) =>
  state.im.isServiceAccountsLoading;
export const imAsPeerIdSelector = (state: RootState) => state.im.asPeerId;

export const imBroadcastActiveFilterSelector = createSelector(
  imSelector,
  (_, filterName: BroadcastFilterName) => filterName,
  (im, filterName) => {
    return !!im.broadcast.activeFilters[filterName];
  },
);

export const imHistorySelector = createSelector(
  imSelector,
  (_, peerId: number) => peerId,
  (im, peerId) => {
    return im.history[peerId] || {};
  },
);

export const imChatInfoSelector = createSelector(
  imSelector,
  (_, peerId: number) => peerId,
  (im, peerId) => {
    return im.info[peerId] || { isLoading: true };
  },
);

export const imFormSelector = createSelector(
  imSelector,
  (_, formId: string) => formId,
  (im, formId) => {
    return im.forms[formId] || { message: '' };
  },
);

export const imTypingSelector = createSelector(
  imSelector,
  (_, peerId: number) => peerId,
  (im, peerId) => {
    return !!im.typing[peerId];
  },
);

export const imInfoLoadingSelector = createSelector(
  imSelector,
  (_, peerId: number) => peerId,
  (im, peerId) => {
    return im.info[peerId] ? im.info[peerId].isLoading : true;
  },
);

export const imShowSubscriptionRequiredMessageSelector = createSelector(
  imSelector,
  (_, peerId: number) => peerId,
  (im, peerId) => {
    return !!im.showSubscriptionRequiredMessage[peerId];
  },
);
